import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Coins, X } from 'lucide-react';
import { IpeBrandMark } from '@/components/ipe/IpeBrandLogo';
import CreditPlanSection from './components/CreditPlanSection';
import {
  IPE_MODULE_CREDITS,
  buildCreditConsumptionCatalog,
  formatCreditAmount,
  setRuntimeModuleCredits,
} from '@/constants/ipeCredits';
import {
  HUB_NAV_IDS,
  NAV_HUBS,
  TOOL_NAV_ITEMS,
} from '@/pages/ipe/workbench/constants/workbenchNavConfig';
import { fundingAuthAPI } from '@/utils/api';
import '@/styles/ipe/credit-consumption.css';

function CreditCostDisplay({ row }) {
  const billingUnit = row.billingUnit || '次';
  const resolutionPrices = row.resolutionPrices && typeof row.resolutionPrices === 'object'
    ? row.resolutionPrices
    : null;
  const tiers = ['480P', '720P', '1080P'];
  const hasResolutionPricing = row.supportsResolutionPricing === true
    || (resolutionPrices && tiers.some((t) => Number(resolutionPrices[t]) > 0));

  if (billingUnit === '按步骤') {
    return <span className="ipe-credit-consumption__badge ipe-credit-consumption__badge--step">按步骤扣费</span>;
  }

  if (billingUnit === '免费') {
    return <span className="ipe-credit-consumption__badge ipe-credit-consumption__badge--free">免费</span>;
  }

  if (hasResolutionPricing && resolutionPrices) {
    return (
      <div className="ipe-credit-consumption__cost-wrap ipe-credit-consumption__cost-wrap--tiers">
        {tiers.map((tier) => {
          const cost = Number(resolutionPrices[tier]) || 0;
          return (
            <div key={tier} className="ipe-credit-consumption__tier">
              <span className="ipe-credit-consumption__tier-label">{tier}</span>
              <strong className="ipe-credit-consumption__cost">{formatCreditAmount(cost)}</strong>
            </div>
          );
        })}
        <span className="ipe-credit-consumption__cost-unit">IPE豆 / {billingUnit}</span>
      </div>
    );
  }

  const cost = Number(row.creditCost) || 0;
  if (cost <= 0) {
    return <span className="ipe-credit-consumption__badge ipe-credit-consumption__badge--free">免费</span>;
  }

  return (
    <div className="ipe-credit-consumption__cost-wrap">
      <strong className="ipe-credit-consumption__cost">{formatCreditAmount(cost)}</strong>
      <span className="ipe-credit-consumption__cost-unit">IPE豆 / {billingUnit}</span>
    </div>
  );
}

export default function CreditConsumptionPage() {
  const [searchParams] = useSearchParams();
  const consumptionRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [accountCenter, setAccountCenter] = useState(null);
  const [apiModules, setApiModules] = useState(IPE_MODULE_CREDITS);

  const loadModuleCredits = useCallback(async () => {
    try {
      const list = await fundingAuthAPI.getModuleCredits();
      if (Array.isArray(list) && list.length) {
        setApiModules(list);
        setRuntimeModuleCredits(list);
        return;
      }
    } catch {
      // 公开接口失败时回退
    }
    setApiModules(IPE_MODULE_CREDITS);
  }, []);

  const loadAccountCenter = useCallback(async () => {
    try {
      const data = await fundingAuthAPI.getAccountCenter();
      setAccountCenter(data);
      if (data?.moduleCredits?.length) {
        setApiModules(data.moduleCredits);
        setRuntimeModuleCredits(data.moduleCredits);
      }
    } catch {
      setAccountCenter(null);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      await loadModuleCredits();
      await loadAccountCenter();
    } catch (e) {
      setError(e?.message || '加载失败，已展示默认定价');
    } finally {
      setLoading(false);
    }
  }, [loadModuleCredits, loadAccountCenter]);

  useEffect(() => {
    document.title = 'IPE豆消耗说明 - IPE';
    load();
  }, [load]);

  useEffect(() => {
    if (loading || searchParams.get('scroll') !== 'consumption') return undefined;
    const timer = window.setTimeout(() => {
      consumptionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
    return () => window.clearTimeout(timer);
  }, [loading, searchParams]);

  const catalogGroups = useMemo(
    () => buildCreditConsumptionCatalog(apiModules, {
      toolNavItems: TOOL_NAV_ITEMS,
      navHubs: NAV_HUBS,
      hubNavIds: HUB_NAV_IDS,
    }),
    [apiModules],
  );

  return (
    <div className="ipe-credit-consumption">
      <header className="ipe-credit-consumption__topbar">
        <IpeBrandMark
          className="ipe-credit-consumption__brand"
          textClassName="ipe-credit-consumption__brand-text"
          text="IPE"
        />
        <button
          type="button"
          className="ipe-credit-consumption__close"
          onClick={() => window.close()}
          aria-label="关闭页面"
        >
          <X size={18} strokeWidth={2} aria-hidden="true" />
          关闭
        </button>
      </header>

      <main className="ipe-credit-consumption__main">
        <CreditPlanSection accountCenter={accountCenter} />

        <div
          id="ipe-credit-consumption-catalog"
          ref={consumptionRef}
          className="ipe-credit-consumption__hero"
        >
          <span className="ipe-credit-consumption__hero-icon" aria-hidden="true">
            <Coins size={28} strokeWidth={2} />
          </span>
          <div>
            <h1 className="ipe-credit-consumption__title">功能IPE豆消耗说明</h1>
          </div>
        </div>

        {error ? <p className="ipe-credit-consumption__notice">{error}</p> : null}

        {loading ? (
          <div className="ipe-credit-consumption__loading">加载中…</div>
        ) : (
          <div className="ipe-credit-consumption__catalog">
            {catalogGroups.map((group) => (
              <section key={group.title} className="ipe-credit-consumption__group">
                <h2 className="ipe-credit-consumption__group-title">{group.title}</h2>
                <ul className="ipe-credit-consumption__list">
                  {group.items.map((row) => (
                    <li key={row.navId} className="ipe-credit-consumption__item">
                      <div className="ipe-credit-consumption__item-main">
                        <span className="ipe-credit-consumption__item-label">{row.label}</span>
                        {row.refNavId ? (
                          <span className="ipe-credit-consumption__item-ref">
                            计费同 {TOOL_NAV_ITEMS[row.refNavId]?.label || row.refNavId}
                          </span>
                        ) : null}
                      </div>
                      <CreditCostDisplay row={row} />
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}

        <p className="ipe-credit-consumption__foot">
          工作流任务按所含步骤逐步扣费；按张计费的功能在批量处理时按图片数量累计。充值IPE豆与会员赠送豆共享使用，有效期以购买套餐为准。
          个人工作区消耗个人额度，团队工作区消耗团队额度；可在工作台「账单中心 → 消耗流水」查看每笔扣费归属。
        </p>
      </main>
    </div>
  );
}
